import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import StockMovementsTable from './StockMovementsTable';
import AddStockMovementModal from './AddStockMovementModal';
import ViewStockMovementModal from './ViewStockMovementModal';
import { getStockMovements, getStockSummary } from '../../../services/StockService';
import MenuContext from '../../../context/MenuContext';
import { useTranslation } from 'react-i18next';
import CustomTabPanel from '../../../components/Tab/CustomTabPanel';
import CurrentStockTable from './CurrentStockTable';

const Stocks = () => {
  const { t } = useTranslation();
  const { fetchMenu } = useContext(MenuContext);

  const [movements, setMovements] = useState([]);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [movementsPagination, setMovementsPagination] = useState({ page: 0, pageSize: 10 });
  const [movementsSortModel, setMovementsSortModel] = useState([]);
  const [movementsFilterModel, setMovementsFilterModel] = useState({ items: [] });
  const [movementsSearchTerm, setMovementsSearchTerm] = useState('');
  const [movementsTotalRows, setMovementsTotalRows] = useState(0);

  const [summary, setSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summaryPagination, setSummaryPagination] = useState({ page: 0, pageSize: 10 });
  const [summarySortModel, setSummarySortModel] = useState([]);
  const [summaryFilterModel, setSummaryFilterModel] = useState({ items: [] });
  const [summarySearchTerm, setSummarySearchTerm] = useState('');
  const [summaryTotalRows, setSummaryTotalRows] = useState(0);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // tabs
  const [tabValue, setTabValue] = useState(0);
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  // Refs para manejar debounce
  const movementsSearchTimeoutRef = useRef(null);
  const summarySearchTimeoutRef = useRef(null);

  const fetchMovements = useCallback(async (page, pageSize, search, currentSortModel = movementsSortModel, currentFilterModel = movementsFilterModel) => {
    try {
      setLoading(true);
      const cleanSearch = search || '';
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const response = await getStockMovements(page, pageSize, cleanSearch, null, {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
      });
      
      // El backend devuelve { movements: [...], total: number, page: number, pageSize: number }
      if (response && typeof response === 'object') {
        setMovements(response.movements || response.data || []);
        setMovementsTotalRows(response.total || response.totalCount || 0);
      } else {
        // Fallback para compatibilidad con respuesta anterior
        setMovements(response || []);
        setMovementsTotalRows(response?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching stock movements:', error);
      setMovements([]);
      setMovementsTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [movementsSortModel, movementsFilterModel]);

  const fetchSummary = useCallback(async (page, pageSize, search, currentSortModel = summarySortModel, currentFilterModel = summaryFilterModel) => {
    try {
      setLoadingSummary(true);
      const cleanSearch = search || '';
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const response = await getStockSummary(page, pageSize, cleanSearch, {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
      });
      
      // El servicio ya devuelve response.data.data, que contiene el objeto con summaries y total
      if (response && typeof response === 'object') {
        const data = response.summaries || response.summary || response.data || response.stocks || [];
        const total = response.total || response.totalCount || response.count || data.length;
        
        setSummary(data);
        setSummaryTotalRows(total);
      } else {
        // Fallback
        setSummary([]);
        setSummaryTotalRows(0);
      }
    } catch (error) {
      console.error('Error fetching stock summary:', error);
      setSummary([]);
      setSummaryTotalRows(0);
    } finally {
      setLoadingSummary(false);
    }
  }, [summarySortModel, summaryFilterModel]);

  // Cargar datos iniciales solo una vez
  useEffect(() => {
    fetchMovements(movementsPagination.page + 1, movementsPagination.pageSize, movementsSearchTerm);
    fetchSummary(summaryPagination.page + 1, summaryPagination.pageSize, summarySearchTerm);
    
    // Cleanup timeouts al desmontar
    return () => {
      if (movementsSearchTimeoutRef.current) {
        clearTimeout(movementsSearchTimeoutRef.current);
      }
      if (summarySearchTimeoutRef.current) {
        clearTimeout(summarySearchTimeoutRef.current);
      }
    };
  }, [fetchMovements, fetchSummary]);

  // Funciones para manejar cambios de paginación
  const handleMovementsPaginationChange = (newModel) => {
    setMovementsPagination(newModel);
  };

  const handleSummaryPaginationChange = (newModel) => {
    setSummaryPagination(newModel);
  };

  const handleMovementsSortModelChange = (newSortModel) => {
    setMovementsSortModel(newSortModel);
    setMovementsPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleMovementsFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);
    setMovementsFilterModel({ ...newFilterModel, items: normalizedItems });
    setMovementsPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleSummarySortModelChange = (newSortModel) => {
    setSummarySortModel(newSortModel);
    setSummaryPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleSummaryFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);
    setSummaryFilterModel({ ...newFilterModel, items: normalizedItems });
    setSummaryPagination((prev) => ({ ...prev, page: 0 }));
  };

  // Funciones para manejar cambios de búsqueda con debounce
  const handleMovementsSearchChange = useCallback((searchTerm) => {
    setMovementsSearchTerm(searchTerm);
    setMovementsPagination(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (movementsSearchTimeoutRef.current) {
      clearTimeout(movementsSearchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    movementsSearchTimeoutRef.current = setTimeout(() => {
      fetchMovements(1, movementsPagination.pageSize, searchTerm, movementsSortModel, movementsFilterModel);
    }, 500);
  }, [fetchMovements, movementsPagination.pageSize, movementsSortModel, movementsFilterModel]);

  const handleSummarySearchChange = useCallback((searchTerm) => {
    setSummarySearchTerm(searchTerm);
    setSummaryPagination(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (summarySearchTimeoutRef.current) {
      clearTimeout(summarySearchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    summarySearchTimeoutRef.current = setTimeout(() => {
      fetchSummary(1, summaryPagination.pageSize, searchTerm, summarySortModel, summaryFilterModel);
    }, 500);
  }, [fetchSummary, summaryPagination.pageSize, summarySortModel, summaryFilterModel]);

  const handleAddMovement = async (newMovement) => {
    try {
      setMovements([newMovement, ...movements]);
      fetchMenu();
    } catch (error) {
      console.error('Error creating stock movement:', error);
    }
  };

  return (
    <>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleChangeTab}>
          <Tab label={t('current_stock')} />
          <Tab label={t('stock_movements')} />
        </Tabs>
      </Box>

      {/* Current Stock */}
      <CustomTabPanel value={tabValue} index={0}>
        <CurrentStockTable 
          stocks={summary} 
          loading={loadingSummary}
          paginationModel={summaryPagination}
          onPaginationModelChange={handleSummaryPaginationChange}
          sortModel={summarySortModel}
          onSortModelChange={handleSummarySortModelChange}
          filterModel={summaryFilterModel}
          onFilterModelChange={handleSummaryFilterModelChange}
          searchTerm={summarySearchTerm}
          onSearchChange={handleSummarySearchChange}
          onRefresh={() => fetchSummary(summaryPagination.page + 1, summaryPagination.pageSize, summarySearchTerm, summarySortModel, summaryFilterModel)}
          totalRows={summaryTotalRows}
        />
      </CustomTabPanel>

      {/* Movements */}
      <CustomTabPanel value={tabValue} index={1}>
        <StockMovementsTable 
          loading={loading}
          movements={movements}
          paginationModel={movementsPagination}
          onPaginationModelChange={handleMovementsPaginationChange}
          sortModel={movementsSortModel}
          onSortModelChange={handleMovementsSortModelChange}
          filterModel={movementsFilterModel}
          onFilterModelChange={handleMovementsFilterModelChange}
          searchTerm={movementsSearchTerm}
          onSearchChange={handleMovementsSearchChange}
          onRefresh={() => fetchMovements(movementsPagination.page + 1, movementsPagination.pageSize, movementsSearchTerm, movementsSortModel, movementsFilterModel)}
          totalRows={movementsTotalRows}
          onAdd={() => setIsAddModalOpen(true)}
          onView={(movement) => {
            setSelectedMovement(movement);
            setIsViewModalOpen(true);
          }}
        />

        <AddStockMovementModal
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleAddMovement}
        />
        <ViewStockMovementModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          movement={selectedMovement}
        />
      </CustomTabPanel>
    </>
  );
};

export default Stocks;
